import { useEffect } from 'react';

const LOGO_URL  = `${window.location.origin}/logo.png`;
const SITE_NAME = 'Emax Technologies';
const BASE_DESC = 'Emax Technologies is a leading SAP & ERP training institute in Hyderabad offering classroom and online courses in SAP FICO, MM, ABAP, HANA, UI5/FIORI and Data Science.';

/**
 * Sets <title>, meta description, og:* and twitter:* tags for the current page.
 * Restores the previous title when the component unmounts.
 *
 * @param {object} opts
 * @param {string} opts.title        - Full browser tab title
 * @param {string} [opts.description] - Page-specific description (falls back to BASE_DESC)
 * @param {string} [opts.ogTitle]    - og:title  (falls back to opts.title)
 * @param {string} [opts.ogImage]    - og:image  (falls back to logo)
 */
export function usePageMeta({ title, description, ogTitle, ogImage } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    const desc   = description || BASE_DESC;
    const ogT    = ogTitle     || title;
    const ogImg  = ogImage     || LOGO_URL;
    const ogUrl  = window.location.href;

    document.title = title;

    setMeta('description',         desc);
    setMeta('og:title',            ogT,   'property');
    setMeta('og:description',      desc,  'property');
    setMeta('og:image',            ogImg, 'property');
    setMeta('og:url',              ogUrl, 'property');
    setMeta('og:site_name',        SITE_NAME, 'property');
    setMeta('twitter:title',       ogT);
    setMeta('twitter:description', desc);
    setMeta('twitter:image',       ogImg);

    return () => { document.title = prevTitle; };
  }, [title, description, ogTitle, ogImage]);
}

/** Upserts a <meta> tag by name or property attribute. */
function setMeta(key, content, attr = 'name') {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/* ── Per-page metadata presets ──────────────────────────────────────────── */
export const PAGE_META = {
  login: {
    title:       `Login | ${SITE_NAME}`,
    description: 'Sign in to the Emax Technologies Institute Management System.',
  },
  dashboard: {
    title:       `Dashboard | ${SITE_NAME}`,
    description: 'Overview of leads, students, revenue and upcoming follow-ups at Emax Technologies.',
  },
  crm: {
    title:       `CRM Management | ${SITE_NAME}`,
    description: 'Manage leads, follow-ups, tasks and customer relationships for Emax Technologies.',
  },
  students: {
    title:       `Student Management | ${SITE_NAME}`,
    description: 'View and manage all enrolled students, their courses, batches and fee details.',
  },
  studentProfile: {
    title:       `Student Profile | ${SITE_NAME}`,
    description: 'Detailed student profile including enrollment history, fee payments and attendance.',
  },
  courses: {
    title:       `Course Management | ${SITE_NAME}`,
    description: 'Manage SAP & ERP courses, fees, batches and schedules at Emax Technologies.',
  },
  fees: {
    title:       `Fee Management | ${SITE_NAME}`,
    description: 'Track student fee invoices, installment payments, receipts and outstanding dues.',
  },
  attendance: {
    title:       `Attendance Management | ${SITE_NAME}`,
    description: 'Mark and review student attendance across batches with trends and history.',
  },
  staff: {
    title:       `Staff Management | ${SITE_NAME}`,
    description: 'Manage staff accounts, roles and access permissions at Emax Technologies.',
  },
  trainers: {
    title:       `Trainer Management | ${SITE_NAME}`,
    description: 'Manage trainer profiles, assigned batches and course expertise.',
  },
  leadStatus: {
    title:       `Lead Pipeline | ${SITE_NAME} CRM`,
    description: 'Track leads by stage: new, in-progress, interested, attended and enrolled.',
  },
  enquiry: {
    title:       `Student Enquiry Form | ${SITE_NAME} – SAP & ERP Training Hyderabad`,
    description: 'Interested in SAP or ERP training? Fill in our student enquiry form and our team will contact you within 24 hours.',
  },
  resetPassword: {
    title:       `Reset Password | ${SITE_NAME}`,
    description: 'Set a new password for your Emax Technologies Institute Management System account.',
  },
};
